import { OperationVariables } from "@apollo/client";
import { ReactElement } from "react";
import { type LazyQueryResult, type QueryResult } from "src/apolloHooks";
import { Loading } from "src/components/Loading";
import { Error } from "src/routes/Error";

type QueryResultOpts<T> = {
  loading?: () => ReactElement;
  error?: (err: Error) => ReactElement;
  data: (data: T) => ReactElement;
  /**
   * Allows configuring when the loading spinner is shown.
   *
   * By default, we use "first", which means the loading spinner is shown on first render,
   * but then any subsequent renders, whether from a variables changes (officially a new request)
   * or a refetch w/o a variable change (not a new request, so will not re-render anyway without
   * notifyOnNetworkStatusChange enabled), will show the previous data.
   *
   * We default to this b/c the assumption is most requests are fast enough that we should
   * continue showing the old data instead of flashing the loader spinner.
   *
   * However, if you know your request is particularly expensive, and the user will wonder
   * "wtf is happening?", you can opt-in to immediately showing the loading spinner on
   * every refetch by setting this to "always" (note if you're not changing the variables,
   * when you'll probably have to set notifyOnNetworkStatusChange as well).
   *
   * Also, if for some reason you want to disable the loading spinner entirely, you can
   * pass `showLoading: "never"`, and instead we'll render empty content.
   */
  showLoading?: "first" | "always" | "never";
  /** Set if the query pass in is conditionally skipped.
   * If `true`, the loading view will be shown until the query's data is populated.
   * If `false`, it will result in throwing an error (maybe this should never happen?).
   * Defaults to false */
  skippable?: boolean;
};

/** Conditionally renders the loading/error/data boilerplate with default loading/error components. */
export function queryResult<T>(
  result: QueryResult<T, any> | LazyQueryResult<T, any>,
  fn: (data: T) => ReactElement,
): ReactElement;
export function queryResult<T>(
  result: QueryResult<T, any> | LazyQueryResult<T, any>,
  opts: QueryResultOpts<T>,
): ReactElement;
export function queryResult<T>(
  result: QueryResult<T, any> | LazyQueryResult<T, any>,
  fnOrOpts: QueryResultOpts<T> | ((data: T) => ReactElement),
): ReactElement {
  const { loading, error, data, previousData } = result;
  // v4's `useQuery` result has no `called` (only lazy queries do), so default to `false`:
  // a regular query only reaches the terminal `!called` branch below when it was `skip`ped,
  // which should render nothing rather than the "No data" error.
  const called = "called" in result ? result.called : false;
  const dataFn = typeof fnOrOpts === "function" ? fnOrOpts : fnOrOpts.data;
  const opts = typeof fnOrOpts === "function" ? { data: dataFn } : fnOrOpts;
  const {
    showLoading = "first",
    loading: loadingFn = () => <Loading />,
    error: errorFn = (err: Error) => <Error error={err} />,
    skippable = false,
  } = opts;
  // Typically we let `data` or `previousData` take precedence over `loading`, so avoid
  // short-term UX flashing, but if you know you've got a long query, setting
  // `showLoading = "always"` will push loading to the front of the list.
  const mustLoad = loading && showLoading === "always";
  if (error) {
    return errorFn(error);
  } else if (data && !mustLoad) {
    return dataFn(data);
  } else if (previousData && !mustLoad) {
    return dataFn(previousData);
  } else if (loading) {
    if (showLoading === "never") {
      return <></>;
    } else {
      return loadingFn();
    }
  } else if (!called) {
    // if a wrapped query was `skip`ped and hasn't even been called yet, render nothing
    return <></>;
  } else {
    return skippable ? loadingFn() : errorFn(new global.Error("No data"));
  }
}

/**
 * Conditionally renders the loading/error/data boilerplate with default loading/error components.
 *
 * However, this version works with the `cache-and-network` fetch policy, by prioritizing the availability
 * of data over whether or not the query is `loading`.
 *
 * We may be able to replace `queryResult` with this implementation, but wanted to limit the scope of change for now.
 */
export function queryResultDataFirst<T>(
  result: QueryResult<T, any> | LazyQueryResult<T, OperationVariables>,
  opts: QueryResultOpts<T>,
): ReactElement {
  const { loading, error, data } = result;
  if (data) {
    return opts.data(data);
  } else if (loading) {
    return opts.loading ? opts.loading() : <Loading />;
  } else if (error) {
    return <Error error={error} />;
  } else {
    const error = new global.Error("No data");
    return <Error error={error} />;
  }
}

/** Conditionally renders the loading/error/data for multiple queries. */
export function queriesResult<T extends ReadonlyArray<QueryResult<any, any>>>(
  queries: T,
  opts: {
    loading?: () => ReactElement;
    error?: (err: Error) => ReactElement;
    data: (...data: { [Q in keyof T]: T[Q] extends { data: infer U | undefined } ? U : never }) => ReactElement;
  },
): ReactElement {
  if (queries.some((q) => q.loading)) {
    return opts.loading ? opts.loading() : <Loading />;
  } else if (queries.some((q) => q.error !== undefined)) {
    return <Error error={queries.find((q) => q.error !== undefined)!.error!} />;
  } else {
    return opts.data(...(queries.map((q) => q.data) as any));
  }
}

/** Provides a type-guard for checking non-loading/non-error. */
export function hasData<T>(result: QueryResult<T, any>): result is Omit<QueryResult<T, any>, "data"> & { data: T } {
  const { error, data } = result;
  // If we have fetchPolicy:'cache-and-network' set on the query, the data may be present but the cache still empty
  return !error && !!data && Object.keys(data).length > 0;
}

export function renderLoadingOrError(result: QueryResult<any, any>): ReactElement {
  const { error, loading } = result;
  if (loading) {
    return <Loading />;
  } else if (error) {
    return <Error error={error} />;
  } else {
    throw new global.Error(`Expected ${loading} or ${error} to be truthy. Maybe the query is skipped?`);
  }
}
